/* eslint-disable @typescript-eslint/no-explicit-any */
import {
Grid,
TextField,
FormControl,
FormControlLabel,
FormLabel,
Radio,
RadioGroup,
Stack,
Typography,
MenuItem,
} from '@mui/material';
import { formValues } from '../../../utils/types';
import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import QualificationDialog from '../QualificationDialog';
import { formatPhoneNumber } from '../../../utils/utilFuntions';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

function MedicareFields({
values,
setValues,
errors,
setValidStep,
isMedicarePartD,
setIsMedicarePartD,
isMedicarePartC,
setIsMedicarePartC,
isSpouseOnSSC,
}: {
values: formValues;
setValues: React.Dispatch<React.SetStateAction<formValues>>;
errors: { [key: string]: string };
setValidStep: React.Dispatch<React.SetStateAction<boolean>>;
isMedicarePartD: boolean;
setIsMedicarePartD: React.Dispatch<React.SetStateAction<boolean>>;
isMedicarePartC: boolean;
setIsMedicarePartC: React.Dispatch<React.SetStateAction<boolean>>;
isSpouseOnSSC: boolean;
}) {
const [openCostDialog, setOpenCostDialog] = useState<boolean>(false);
const handleClickOpen = () => {
  setOpenCostDialog(true);
};
const [spouseOnSSC, setSpouseOnSSC] = useState<boolean>(true);
const handleClose = () => {
  setOpenCostDialog(false);
  // setSelectedValue(value);
};
const qualificationDialogText = useRef<string>('without Medicare covarage');
const handleMedicareChange = useCallback(
  (value: string | null, name: string) => {
    setValues((prev) => ({
      ...prev,
      patient: {
        ...prev.patient,
        medicare: {
          ...prev.patient.medicare,
          [name]: value,
        },
      },
    }));
  },
  [setValues]
);
const handleMedicareInfoChange = useCallback(
  (value: string | null, name: string) => {
    setValues((prev) => ({
      ...prev,
      patient: {
        ...prev.patient,
        medicare: {
          ...prev.patient.medicare,
          medicareInfo: {
            ...prev.patient.medicare.medicareInfo,
            [name]: value,
          },
        },
      },
    }));
  },
  [setValues]
);
const handlePharmacyInfoChange = useCallback(
  (value: string | null, name: string) => {
    setValues((prev) => ({
      ...prev,
      patient: {
        ...prev.patient,
        medicare: {
          ...prev.patient.medicare,
          pharmacyInfo: {
            ...prev.patient.medicare.pharmacyInfo,
            [name]: value,
          },
        },
      },
    }));
  },
  [setValues]
);
dayjs.extend(isBetween);


const MedicareDateField = useMemo(() => {
  return (
    label: string,
    name:
      | keyof formValues['patient']['medicare']['medicareInfo']
      | keyof formValues['patient']['medicare']['pharmacyInfo'],
    value: formValues,
    errors: { [key: string]: string },
    onChange: (value: string) => void,
    isStartDate?: boolean
  ) => {
    const formatDateInput = (input: string) => {
      // Remove non-numeric characters
      const numbersOnly = input.replace(/\D/g, '');

      let formatted = '';
      let mm = '';
      let dd = '';
      let yyyy = '';

      if (numbersOnly.length >= 1) {
        mm = numbersOnly.slice(0, 2);
        if (parseInt(mm, 10) > 12) mm = '12'; // Ensure month max is 12
        formatted = mm;
      }
      if (numbersOnly.length >= 3) {
        dd = numbersOnly.slice(2, 4);
        if (parseInt(dd, 10) > 31) dd = '31'; // Ensure day max is 31
        formatted = `${mm}/${dd}`;
      }
      if (numbersOnly.length >= 5) {
        yyyy = numbersOnly.slice(4, 8);
        formatted = `${mm}/${dd}/${yyyy}`;
      }

      return formatted;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const formattedValue = formatDateInput(rawValue);
      onChange(formattedValue);

      const isFullDate = /^\d{2}\/\d{2}\/\d{4}$/.test(formattedValue);
      if (!isFullDate) {
        errors[name] = 'Invalid date format';
        setValidStep(false);
        return;
      }
      const inputDate = dayjs(formattedValue, 'MM/DD/YYYY', true);
      // Only run validation when full date is entered
      if (inputDate.isValid()) {
        const today = dayjs();
        const thirtyYearsAgo = today.subtract(30, 'year');

        if (isStartDate) {
          if (inputDate.isBefore(thirtyYearsAgo)) {
            // Handle invalid start date
            errors[name] = 'Start date must be within the last 30 years';
            setValidStep(false);
            return;
          } else if (inputDate.isAfter(today)) {
            // Handle invalid start date
            errors[name] = 'Start date must be before today';
            setValidStep(false);
            return;
          } else {
            errors[name] = '';
            setValidStep(true);
          }
        } else if (isStartDate === false) {
          if (inputDate.isBefore(today)) {
            // Handle invalid end date
            errors[name] = 'End date must be after today';
            setValidStep(false);
          } else {
            errors[name] = '';
            setValidStep(true);
          }
        } else {
          errors[name] = '';
        }
      }
    };

    return (
      <>
        <TextField
          label={label}
          name={name}
          value={
            name.includes('rx')
              ? value.patient.medicare.pharmacyInfo[
                  name as keyof typeof value.patient.medicare.pharmacyInfo
                ] || ''
              : value.patient.medicare.medicareInfo[
                  name as keyof typeof value.patient.medicare.medicareInfo
                ] || ''
          }
          onChange={handleChange}
          variant="outlined"
          fullWidth
          margin="normal"
          error={!!errors[`${name}`]}
          helperText={errors[`${name}`]}
          placeholder="MM/DD/YYYY"
          inputProps={{ maxLength: 10 }}
        />
      </>
    );
  };
}, [setValidStep]);

return (
  <>
    {isSpouseOnSSC && (
      <Grid item xs={12} md={3} key={'SpouseSSC'}>
        <FormControl required>
          <FormLabel id="Are Both Spouses on Social Security Checks">
            Are Both Spouses on Social Security Checks?
          </FormLabel>
          <RadioGroup
            aria-labelledby="Are Both Spouses on Social Security Checks"
            name="bothSSC"
            sx={{ mt: 2 }}
            value={values.patient.medicare['bothSSC']}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handleMedicareChange(e.target.value, 'bothSSC');
              if (e.target.value === 'no') {
                setSpouseOnSSC(false);
                setValues((prev) => ({
                  ...prev,
                  patient: {
                    ...prev.patient,
                    medicare: {
                      ...prev.patient.medicare,
                      spouceRecieveSSC: 'no',
                    },
                  },
                }));
              } else {
                setSpouseOnSSC(true);
              }
            }}>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                value="no"
                control={<Radio />}
                label="No"
                sx={{
                  pr: 2,
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                }}
              />
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label="Yes"
                sx={{
                  pr: 2,
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                }}
              />
            </Stack>
          </RadioGroup>
        </FormControl>
      </Grid>
    )}
    {!spouseOnSSC && isSpouseOnSSC && (
      <Grid item xs={12} md={3} key={'spouceRecieveSSC'}>
        <FormControl required>
          <FormLabel id="spouceRecieveSSC">
            Is one of the spouses going to receive Social Security Checks in
            the upcoming 3 months?
          </FormLabel>
          <RadioGroup
            aria-labelledby="Is one of the spouses going to receive Social Security Checks in the upcoming 3 months? "
            name="spouceRecieveSSC"
            sx={{ mt: 2 }}
            value={values.patient.medicare['spouceRecieveSSC']}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handleMedicareChange(e.target.value, 'spouceRecieveSSC');
              if (e.target.value === 'no') {
                setValidStep(false);
                (qualificationDialogText.current =
                  'Households with only one spouse on Social Security Checks'),
                  handleClickOpen();
              }
            }}>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                value="no"
                control={<Radio />}
                label="No"
                sx={{
                  pr: 2,
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                }}
              />
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label="Yes"
                sx={{
                  pr: 2,
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                }}
              />
            </Stack>
          </RadioGroup>
        </FormControl>
      </Grid>
    )}


<Grid item xs={12} md={3} key={'medicare advantage'}>
        <FormControl required fullWidth>
          <FormLabel id="medicare advantage">
          Are you on  Medicare Advantage / Part C (HMO/PPO plan card)
          </FormLabel>
          <RadioGroup
            aria-labelledby="medicare advantage"
            name="advantage"
            sx={{ mt: 2 }}
            value={values.patient.medicare['advantage']}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const newAdvantageValue = e.target.value;
              handleMedicareChange(newAdvantageValue, 'advantage');
              if (newAdvantageValue === 'yes') {
                setIsMedicarePartC(true);
              } else {
                setIsMedicarePartC(false);
              }
              // Clear partD when advantage changes
              handleMedicareChange('', 'partD');
              setIsMedicarePartD(false);
            }}>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                value="no"
                control={<Radio />}
                label="No"
                sx={{
                  pr: 2,
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                }}
              />
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label="Yes"
                sx={{
                  pr: 2,
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                }}
              />
            </Stack>
          </RadioGroup>
        </FormControl>
      </Grid>
      
    {values.patient.medicare['advantage'] && (
      <Grid item xs={12} md={3} key={'medicare partD'}>
        <TextField
          required
          fullWidth
          select
          label="Choose which prescription drug coverage tied to the Medicare?"
          name="partD"
          value={values.patient.medicare['partD'] || ''}
          error={errors['partD'] ? true : false}
          helperText={errors['partD']}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            handleMedicareChange(value, 'partD');
            // Clear error when value is selected
            if (value && errors['partD']) {
              errors['partD'] = '';
            }
            // Only set isMedicarePartD to true for standalone Part D (PDP), not for MA-PD which is part of Advantage
            if (value === 'PDP') {
              setIsMedicarePartD(true);
            } else if (value === 'no-partD' || value === 'no-prescription-coverage') {
              setIsMedicarePartD(false);
            } else if (value === 'MA-PD') {
              // MA-PD is Part D coverage but through Advantage, so don't set isMedicarePartD
              // This prevents the FirstStep useEffect from clearing advantage
              setIsMedicarePartD(false);
            }
          }}
          variant="outlined"
          sx={{ mt: 2 }}>
          {values.patient.medicare['advantage'] === 'yes' ? (
            [
              <MenuItem key="MA-PD" value="MA-PD">
                MA-PD — drug coverage included in their Advantage plan card (Rx
                BIN/PCN present or the plan says Part D / Prescription Drug)
              </MenuItem>,
              <MenuItem key="no-partD" value="no-partD">
                No Part D / no drug plan
              </MenuItem>,
            ]
          ) : (
            [
              <MenuItem key="PDP" value="PDP">
                PDP — standalone Part D plan (separate card / separate plan
                name)
              </MenuItem>,
              <MenuItem key="no-prescription-coverage" value="no-prescription-coverage">
                No prescription coverage
              </MenuItem>,
            ]
          )}
        </TextField>
      </Grid>
    )}

   
    <Grid
      item
      xs={12}
      md={isMedicarePartC && !isMedicarePartD ? 2 : 3}
      key={'Do you have Medicare Supplement'}>
      <FormControl required>
        <FormLabel id="Do you have Medicare Supplement">
          Do you have Medicare Supplement{' '}
        </FormLabel>
        <RadioGroup
          aria-labelledby="Do you have Medicare Supplement"
          name="supplement"
          sx={{ mt: 2 }}
          value={values.patient.medicare['supplement']}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            handleMedicareChange(e.target.value, 'supplement');
          }}>
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              value="no"
              control={<Radio />}
              label="No"
              sx={{
                pr: 2,
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                backgroundColor: '#fff',
              }}
            />
            <FormControlLabel
              value="yes"
              control={<Radio />}
              label="Yes"
              sx={{
                pr: 2,
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                backgroundColor: '#fff',
              }}
            />
          </Stack>
        </RadioGroup>
      </FormControl>
    </Grid>
    <Grid item xs={12} sm={3} key={'MBI'} mt={4}>
      <TextField
        required={true}
        error={errors['mbi'] ? true : false}
        helperText={errors['mbi']}
        key={'MBI'}
        fullWidth
        name={'mbi'}
        value={values.patient.medicare['mbi'] || ''}
        label={
          'MBI# ( from red, white, & blue ID card - Medicare Beneficiary Identifier)'
        }
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          //format MBI
          const formattedValue = e.target.value
            .toUpperCase() // Ensure all letters are capitalized
            .replace(/[^A-Z0-9]/g, '') // Remove invalid characters
            .slice(0, 11) // Limit to 11 alphanumeric characters
            .replace(/(\w{4})(\w{3})(\w{0,4})/, '$1-$2-$3') // Format as XXXX-XXX-XXXX
            .replace(/-$/, ''); // Remove trailing dash if incomplete
          handleMedicareChange(formattedValue, 'mbi');
        }}
        variant="outlined"
      />
    </Grid>
    {(isMedicarePartD || isMedicarePartC) && (
      <>
        <Grid item xs={12}>
          <Typography variant="h6">
            Medicare Medical Insurance Information
          </Typography>
        </Grid>
        <Grid item xs={12} sm={3} key={'memberId'} mt={2}>
          <TextField
            error={errors['memberId'] ? true : false}
            helperText={errors['memberId']}
            key={'memberId'}
            fullWidth
            name={'memberId'}
            value={values.patient.medicare.medicareInfo['memberId'] || ''}
            label={'Member ID/ Policy Number'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handleMedicareInfoChange(e.target.value, 'memberId');
            }}
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} sm={3} key={'medicareDate'}>
          {MedicareDateField(
            'Medicare Effective Date',
            'medicareDate',
            values,
            errors,
            (value: string) => {
              handleMedicareInfoChange(value, 'medicareDate');
            }
          )}
        </Grid>
        <Grid item xs={12} sm={3} key={'insuranceName'} mt={2}>
          <TextField
            error={errors['insuranceName'] ? true : false}
            helperText={errors['insuranceName']}
            key={'insuranceName'}
            fullWidth
            name={'insuranceName'}
            value={
              values.patient.medicare.medicareInfo['insuranceName'] || ''
            }
            label={'Insurance Name'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handleMedicareInfoChange(e.target.value, 'insuranceName');
            }}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={3} key={'planPhone'} mt={2}>
          <TextField
            error={errors['planPhone'] ? true : false}
            helperText={errors['planPhone']}
            key={'planPhone'}
            fullWidth
            name={'planPhone'}
            value={values.patient.medicare.medicareInfo['planPhone'] || ''}
            label={'Plan Phone Number'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              formatPhoneNumber(e);
              const value = e.target.value;
              handleMedicareInfoChange(value, 'planPhone');
            }}
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} sm={3} key={'groupId'} mt={2}>
          <TextField
            error={errors['groupId'] ? true : false}
            helperText={errors['groupId']}
            key={'groupId'}
            fullWidth
            name={'groupId'}
            value={values.patient.medicare.medicareInfo['groupId'] || ''}
            label={'Group ID / Number'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handleMedicareInfoChange(e.target.value, 'groupId');
            }}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={3} key={'medicarePlanDateStart'}>
          {MedicareDateField(
            'Plan Year Start Date ',
            'planStartDate',
            values,
            errors,
            (value: string) => {
              handleMedicareInfoChange(value, 'planStartDate');
            },
            true
          )}
        </Grid>
        <Grid item xs={12} sm={3} key={'medicarePlanDateEnd'}>
          {MedicareDateField(
            'Plan Year End Date ',
            'planEndDate',
            values,
            errors,
            (value: string) => {
              handleMedicareInfoChange(value, 'planEndDate');
            },
            false
          )}
        </Grid>
      </>
    )}
    {(isMedicarePartD ||
      values.patient.medicare.advantageCoverage === 'yes') && (
      <>
        <Grid item xs={12}>
          <Typography variant="h6">Pharmacy Insurance Information</Typography>
        </Grid>
        <Grid item xs={12} sm={3} key={'rxMemberId'} mt={2}>
          <TextField
            error={errors['rxMemberId'] ? true : false}
            helperText={errors['rxMemberId']}
            key={'rxMemberId'}
            fullWidth
            name={'rxMemberId'}
            value={values.patient.medicare.pharmacyInfo['rxMemberId'] || ''}
            label={'Member ID/ Policy Number'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handlePharmacyInfoChange(e.target.value, 'rxMemberId');
            }}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={3} key={'pcn'} mt={2}>
          <TextField
            error={errors['pcn'] ? true : false}
            helperText={errors['pcn']}
            key={'pcn'}
            fullWidth
            name={'pcn'}
            value={values.patient.medicare.pharmacyInfo['pcn'] || ''}
            label={'PCN ID / Number'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handlePharmacyInfoChange(e.target.value, 'pcn');
            }}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={3} key={'bin'} mt={2}>
          <TextField
            error={errors['bin'] ? true : false}
            helperText={errors['bin']}
            key={'bin'}
            fullWidth
            name={'bin'}
            value={values.patient.medicare.pharmacyInfo['bin'] || ''}
            label={'BIN ID / Number'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handlePharmacyInfoChange(e.target.value, 'bin');
            }}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={3} key={'rxInsuranceName'} mt={2}>
          <TextField
            error={errors['rxInsuranceName'] ? true : false}
            helperText={errors['rxInsuranceName']}
            key={'rxInsuranceName'}
            fullWidth
            name={'rxInsuranceName'}
            value={
              values.patient.medicare.pharmacyInfo['rxInsuranceName'] || ''
            }
            label={'Insurance Name'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handlePharmacyInfoChange(e.target.value, 'rxInsuranceName');
            }}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={3} key={'rxPlanPhone'} mt={2}>
          <TextField
            error={errors['rxPlanPhone'] ? true : false}
            helperText={errors['rxPlanPhone']}
            key={'rxPlanPhone'}
            fullWidth
            name={'rxPlanPhone'}
            value={values.patient.medicare.pharmacyInfo['rxPlanPhone'] || ''}
            label={'Plan Phone Number'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              formatPhoneNumber(e);
              const value = e.target.value;
              handlePharmacyInfoChange(value, 'rxPlanPhone');
            }}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={3} key={'rxGroupId'} mt={2}>
          <TextField
            error={errors['rxGroupId'] ? true : false}
            helperText={errors['rxGroupId']}
            key={'rxGroupId'}
            fullWidth
            name={'rxGroupId'}
            value={values.patient.medicare.pharmacyInfo['rxGroupId'] || ''}
            label={'Group ID / Number'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handlePharmacyInfoChange(e.target.value, 'rxGroupId');
            }}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={3} key={'rxPlanStartDate'}>
          {MedicareDateField(
            'Plan Year Start Date ',
            'rxPlanStartDate',
            values,
            errors,
            (value: string) => {
              handlePharmacyInfoChange(value, 'rxPlanStartDate');
            }
          )}
        </Grid>
        <Grid item xs={12} sm={3} key={'medicarePlanDateEnd'}>
          {MedicareDateField(
            'Plan Year End Date ',
            'rxPlanEndDate',
            values,
            errors,
            (value: string) => {
              handlePharmacyInfoChange(value, 'rxPlanEndDate');
            }
          )}
        </Grid>
      </>
    )}
    <QualificationDialog
      open={openCostDialog}
      handleClose={handleClose}
      qulificationGroup={qualificationDialogText.current}
      isEligible={false}
    />
  </>
);
}

export default MedicareFields;
